export type DetectedAgentOs = 'darwin' | 'linux' | 'windows';
export type DetectedAgentArch = 'amd64' | 'arm64';

export type DetectedAgentTarget = {
  os: DetectedAgentOs;
  arch: DetectedAgentArch;
  /** Catalog download id, e.g. darwin-arm64 */
  id: string;
  /** True when arch was inferred (common on Safari macOS). */
  archGuessed: boolean;
};

type NavigatorUaData = {
  platform?: string;
  architecture?: string;
  getHighEntropyValues?: (hints: string[]) => Promise<{
    architecture?: string;
    bitness?: string;
    platform?: string;
  }>;
};

const normalizeArch = (raw: string | undefined): DetectedAgentArch | null => {
  const value = String(raw || '').toLowerCase();
  if (!value) return null;
  if (value.includes('arm64') || value === 'arm' || value.includes('aarch64')) return 'arm64';
  if (value.includes('x86_64') || value.includes('amd64') || value === 'x86' || value.includes('x64')) {
    return 'amd64';
  }
  return null;
};

const detectOs = (userAgent: string, platformHint: string): DetectedAgentOs | null => {
  const ua = userAgent || '';
  const platform = platformHint || '';
  if (/iphone|ipad|ipod|android/i.test(ua)) return null;
  if (/mac|darwin/i.test(platform) || /Macintosh|Mac OS X/i.test(ua)) return 'darwin';
  if (/win/i.test(platform) || /Windows/i.test(ua)) return 'windows';
  if (/linux/i.test(platform) || (/Linux/i.test(ua) && !/Android/i.test(ua))) return 'linux';
  return null;
};

/**
 * Best-effort browser OS/arch for the agent binary catalog.
 * Prefer navigator.userAgentData when the browser exposes it.
 */
export async function detectAgentDownloadTarget(
  nav: Navigator = typeof navigator !== 'undefined' ? navigator : ({} as Navigator),
): Promise<DetectedAgentTarget | null> {
  const uaData = (nav as Navigator & { userAgentData?: NavigatorUaData }).userAgentData;
  const userAgent = String(nav.userAgent || '');
  let platformHint = String(uaData?.platform || nav.platform || '');
  let arch = normalizeArch(uaData?.architecture);
  let archGuessed = false;

  if (uaData?.getHighEntropyValues) {
    try {
      const high = await uaData.getHighEntropyValues(['architecture', 'bitness', 'platform']);
      if (high.platform) platformHint = high.platform;
      const fromHigh = normalizeArch(high.architecture);
      if (fromHigh) {
        arch = fromHigh;
      } else if (high.bitness === '64' && !arch) {
        // Chrome sometimes omits architecture; 64-bit Intel/AMD is amd64.
        arch = 'amd64';
        archGuessed = true;
      }
    } catch {
      // Keep UA fallback.
    }
  }

  const os = detectOs(userAgent, platformHint);
  if (!os) return null;

  if (!arch) {
    if (os === 'darwin') {
      // Safari still reports MacIntel on Apple silicon; default to arm64 for modern Macs.
      arch = 'arm64';
      archGuessed = true;
    } else if (os === 'windows') {
      arch = 'amd64';
      archGuessed = true;
    } else {
      arch = 'amd64';
      archGuessed = true;
    }
  }

  return {
    os,
    arch,
    id: `${os}-${arch}`,
    archGuessed,
  };
}

export function describeDetectedTarget(
  target: DetectedAgentTarget,
  language: 'en' | 'es',
): string {
  const osLabel = target.os === 'darwin' ? 'macOS' : target.os === 'windows' ? 'Windows' : 'Linux';
  const archLabel = target.arch === 'arm64'
    ? (target.os === 'darwin'
      ? (language === 'es' ? 'Apple silicon (arm64)' : 'Apple silicon (arm64)')
      : 'arm64')
    : (target.os === 'darwin'
      ? (language === 'es' ? 'Intel (amd64)' : 'Intel (amd64)')
      : 'amd64');
  if (language === 'es') {
    return target.archGuessed
      ? `Este navegador parece ${osLabel}. Usamos ${archLabel} (si falla, pruebe la otra arquitectura).`
      : `Este navegador parece ${osLabel} ${archLabel}.`;
  }
  return target.archGuessed
    ? `This browser looks like ${osLabel}. Using ${archLabel} (if that fails, try the other architecture).`
    : `This browser looks like ${osLabel} ${archLabel}.`;
}
